// TenantShell — simpler chrome for tenant-side pages (no admin sidebar, softer
// indigo accent, plain top nav).

import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home as HomeIcon,
  FileText,
  Wrench,
  CreditCard,
  User,
  LogOut,
  Languages,
  Menu,
  X,
} from "lucide-react";

const NAV = [
  { to: "/tenant", label: "Home", Icon: HomeIcon, tid: "tenant-nav-home", exact: true },
  { to: "/tenant/lease", label: "My Lease", Icon: FileText, tid: "tenant-nav-lease" },
  { to: "/tenant/maintenance", label: "Maintenance", Icon: Wrench, tid: "tenant-nav-maintenance" },
  { to: "/tenant/payments", label: "Payments", Icon: CreditCard, tid: "tenant-nav-payments" },
  { to: "/tenant/profile", label: "Profile", Icon: User, tid: "tenant-nav-profile" },
];

export default function TenantShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout?.();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/40 to-white text-slate-900">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 pt-safe backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/tenant" className="flex items-center gap-2" data-testid="tenant-logo">
            <img src="/logo.png" alt="" className="h-7 w-7 rounded" />
            <span className="text-base font-bold tracking-tight">
              runidea
              <span className="ml-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-indigo-700">
                Tenant
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex" data-testid="tenant-nav">
            {NAV.map(({ to, label, Icon, tid, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                data-testid={tid}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <span className="text-xs text-slate-500">{user?.name}</span>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="tenant-logout"
            >
              <LogOut className="h-3 w-3" /> Logout
            </button>
          </div>

          <button
            className="rounded-md border border-slate-200 p-2 md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="tenant-mobile-toggle"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
            <nav className="flex flex-col gap-1">
              {NAV.map(({ to, label, Icon, tid, exact }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={exact}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${tid}-mobile`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-2 py-2 text-sm ${
                      isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-700"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" /> {label}
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                className="mt-2 flex items-center gap-2 rounded-md border border-slate-200 px-2 py-2 text-sm text-slate-700"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </nav>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}
